import {
  ChangeEvent,
  Children,
  Dispatch,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  SetStateAction,
  SyntheticEvent,
  cloneElement,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { FamilyMember } from 'enrollment/features/Shared';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import { SubscriberFieldsDto, ErrorResponseDto } from 'theSource/types';
import { DecisionIQFamilySelectListStyled } from '../../DecisionIQStyled';
import { useApiMutation } from 'theSource/utils';
import { useConfigurationQuery } from 'theSource/queries';
import {
  StyledPrescriptionSearchWrapper,
  StyledPrescriptionSearch,
  StyledPrescriptionLabel,
  StyledPrescriptionChip,
  StyledSearchRecommendations,
} from './PrescriptionSelectStyled';

interface PrescriptionSelectProps {
  showPrescriptionSearch: boolean[] | undefined;
  setShowPrescriptionSearch: Dispatch<SetStateAction<boolean[] | undefined>>;
  dependents?: DependentsResponseDto;
  subscriber?: SubscriberFieldsDto;
  subscriberPrescriptions: string[];
  setSubscriberPrescriptions: Dispatch<SetStateAction<string[]>>;
  dependentPrescriptions: { [key: number]: string[] };
  setDependentPrescriptions: Dispatch<
    SetStateAction<{ [key: number]: string[] }>
  >;
}

interface PrescriptionSearchResponseDto {
  data: {
    Rx_match: string[];
  };
}

export const PrescriptionSelect = ({
  showPrescriptionSearch,
  setShowPrescriptionSearch,
  dependents,
  subscriber,
  subscriberPrescriptions,
  setSubscriberPrescriptions,
  dependentPrescriptions,
  setDependentPrescriptions,
}: PrescriptionSelectProps) => {
  const { t } = useTranslation('pages.decisionIQ');
  const dependentsData = dependents?.data.dependents;
  const [searchValue, setSearchValue] = useState('');
  const [searchRecommendations, setSearchRecommendations] = useState<string[]>([
    '',
  ]);
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const dependentIds = dependents
    ? dependents.data.dependents.map((dependent) => dependent.id)
    : [];

  const handleKeyUp = (
    e: KeyboardEvent<HTMLDivElement>,
    dependentId?: number
  ) => {
    if (e.key === 'Enter') {
      const inputElement = e.target as HTMLInputElement;
      if (
        !inputElement.value ||
        inputElement.value.replaceAll(' ', '') === ''
      ) {
        return;
      }
      if (dependentId) {
        setDependentPrescriptions((oldState) => ({
          ...oldState,
          [dependentId]: [
            ...(oldState[dependentId]?.length ? oldState[dependentId] : []),
            inputElement.value,
          ],
        }));
      } else {
        setSubscriberPrescriptions([
          ...(subscriberPrescriptions?.length ? subscriberPrescriptions : []),
          inputElement.value,
        ]);
      }
    }
  };

  const handleSelection = (
    e: SyntheticEvent<HTMLDivElement>,
    dependentId?: number
  ) => {
    const inputElement = e.target as HTMLInputElement;
    if (dependentId) {
      setDependentPrescriptions((oldState) => ({
        ...oldState,
        [dependentId]: [
          ...(oldState[dependentId]?.length ? oldState[dependentId] : []),
          inputElement.innerText,
        ],
      }));
    } else {
      setSubscriberPrescriptions([
        ...(subscriberPrescriptions?.length ? subscriberPrescriptions : []),
        inputElement.innerText,
      ]);
    }
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchValue(e.target.value);
  };

  const { mutate: searchPrescriptions } = useApiMutation<
    PrescriptionSearchResponseDto,
    ErrorResponseDto,
    { search: string }
  >({
    queryKey: 'searchPrescriptions',
    method: 'post',
    endpoint: `${lumosApiHost}/v1/self_service/rx_search/${searchValue}`,
    getErrorMessage: (error) => error.response?.data?.details,
    onSuccess: (response) => {
      setSearchRecommendations(response.data?.Rx_match || ['']);
    },
    showSuccessSnackbar: false,
  });

  useEffect(() => {
    if (searchValue.length > 2) {
      searchPrescriptions({ search: searchValue });
    }
  }, [searchValue, searchPrescriptions]);

  const handleDelete = (index: number, dependentId?: number) => {
    if (dependentId) {
      const arr = [...dependentPrescriptions[dependentId]];
      arr.splice(index, 1);
      setDependentPrescriptions({
        ...dependentPrescriptions,
        [dependentId]: arr,
      });
    } else {
      const arr = [...subscriberPrescriptions];
      arr.splice(index, 1);
      setSubscriberPrescriptions(arr);
    }
  };

  const handleFamilySelect = (index: number) => {
    if (!showPrescriptionSearch) {
      return;
    }

    const newShowPrescriptionSearch = [...showPrescriptionSearch];
    newShowPrescriptionSearch[index] = !newShowPrescriptionSearch[index];
    setShowPrescriptionSearch(newShowPrescriptionSearch);

    // user has unchecked question, we need to set the lists to empty
    if (showPrescriptionSearch[index]) {
      if (index === 0) {
        setSubscriberPrescriptions([]);
      } else {
        const newDependentPrescriptions = { ...dependentPrescriptions };
        newDependentPrescriptions[dependentIds[index - 1]] = [];
        setDependentPrescriptions(newDependentPrescriptions);
      }
    }
  };

  return (
    <DecisionIQFamilySelectListStyled>
      {/* Cannot use "isYou" since that makes the checkmark disabled, which we don't want */}
      <div className="pb-md-3 mt-3 in-drawer">
        <FamilyMember
          name="You"
          onChangeKey="prescription-0"
          onChangeWithKey={() => handleFamilySelect(0)}
          profile={subscriber?.subscriber_profile}
          selected={subscriberPrescriptions.length > 0}
        />
        {showPrescriptionSearch && showPrescriptionSearch[0] && (
          <div className="prescription-search">
            <label htmlFor="prescription-autocomplete-you">
              {t('prescription_title')}
            </label>
            <StyledPrescriptionSearchWrapper>
              <StyledPrescriptionSearch
                freeSolo
                disablePortal
                id="prescription-autocomplete-you"
                options={searchRecommendations}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by prescription"
                    onChange={handleChange}
                    value={searchValue}
                    onKeyUp={(e: KeyboardEvent<HTMLDivElement>) =>
                      handleKeyUp(e)
                    }
                    // Adding an EndAdornment to the TextField breaks the Autocomplete options
                  />
                )}
                PaperComponent={({ children }) =>
                  searchRecommendations.length > 0 &&
                  searchRecommendations[0] ? (
                    <StyledSearchRecommendations>
                      {Children.map(children, (child) =>
                        child
                          ? cloneElement(child as ReactElement, {
                              onClick: (e: MouseEvent<HTMLDivElement>) =>
                                handleSelection(e),
                            })
                          : child
                      )}
                    </StyledSearchRecommendations>
                  ) : null
                }
              />
              <SearchIcon />
            </StyledPrescriptionSearchWrapper>
            <p>{t('selected')}:</p>
            <div
              className="chips-container d-flex mb-5 mg-md-0"
              style={{ gap: '16px', flexWrap: 'wrap' }}
            >
              {subscriberPrescriptions.map((item, index) => (
                <StyledPrescriptionChip
                  key={`subscriber-${index}`}
                  size="small"
                  onDelete={() => handleDelete(index)}
                  onClick={() => handleDelete(index)}
                  label={
                    <StyledPrescriptionLabel>
                      {item}
                      <span className="sr-only">{t('clickingAction')}</span>
                    </StyledPrescriptionLabel>
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {dependents &&
        dependentsData?.map((dependent, index) => (
          <div className="pb-md-3 in-drawer" key={index}>
            <FamilyMember
              name={dependent.first_name}
              onChangeKey={`prescription-${index + 1}`}
              onChangeWithKey={() => handleFamilySelect(index + 1)}
              profile={dependent.dependent_profile}
              selected={dependentPrescriptions[dependent.id]?.length > 0}
            />
            {showPrescriptionSearch && showPrescriptionSearch[index + 1] && (
              <div className="ml-md-4 p-md-2 prescription-search">
                <label htmlFor={`prescriptionSearch-${dependent.id}`}>
                  {t('prescription_title')}
                </label>
                <StyledPrescriptionSearchWrapper>
                  <StyledPrescriptionSearch
                    freeSolo
                    disablePortal
                    id={`prescription-autocomplete-${dependent.id}`}
                    options={searchRecommendations}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id={`prescription-search-${dependent.id}`}
                        placeholder="Search by prescription"
                        onChange={handleChange}
                        onKeyUp={(e: KeyboardEvent<HTMLDivElement>) =>
                          handleKeyUp(e, dependent.id)
                        }
                        // Adding an EndAdornment to the TextField breaks the Autocomplete options
                      />
                    )}
                    PaperComponent={({ children }) =>
                      searchRecommendations.length > 0 &&
                      searchRecommendations[0] ? (
                        <StyledSearchRecommendations>
                          {Children.map(children, (child) =>
                            child
                              ? cloneElement(child as ReactElement, {
                                  onClick: (e: MouseEvent<HTMLDivElement>) =>
                                    handleSelection(e, dependent.id),
                                })
                              : child
                          )}
                        </StyledSearchRecommendations>
                      ) : null
                    }
                  />
                  <SearchIcon />
                </StyledPrescriptionSearchWrapper>
                <div>
                  <p>{t('selected')}:</p>
                  <div
                    className="chips-container d-flex"
                    style={{ gap: '16px', flexWrap: 'wrap' }}
                  >
                    {dependentPrescriptions[dependent.id].map((item, index) => (
                      <StyledPrescriptionChip
                        key={`dependent-${dependent.id}-${index}`}
                        size="small"
                        onDelete={() => handleDelete(index, dependent.id)}
                        label={
                          <StyledPrescriptionLabel>
                            {item}
                          </StyledPrescriptionLabel>
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
    </DecisionIQFamilySelectListStyled>
  );
};
